import { useEffect, useState, useRef, Fragment } from 'react';
import maplibregl from 'maplibre-gl';
import Map, { Marker, Source, Layer, NavigationControl, FullscreenControl } from 'react-map-gl/maplibre';
import { Dialog, Transition } from "@headlessui/react";
import {apiKeyGeoapify} from '_config/constantes';
import Supercluster from 'supercluster';
import markerRose from "../../images/markerRose.png";
import markerJaune from "../../images/markerJaune.png";
import markerVert from "../../images/markerVert.png";
import markerLoc from "../../images/markerViolet.png";
import markerBleu from "../../images/markerBleu.png";
import { FeatureCollection, Feature, Properties, LineString } from '@turf/helpers';
import * as turf from '@turf/turf';
import { LngLatLike } from 'mapbox-gl';
import { JourneeType } from "data/types";
import { useTranslation } from 'react-i18next';
import Lottie from "lottie-react";
import planeLottie from "../../images/lottie/plane.json";
import carLottie from "../../images/lottie/car.json";
import trainLottie from "../../images/lottie/train.json";
import bicycleLottie from "../../images/lottie/bicycle.json";
import motorbikeLottie from "../../images/lottie/motorbike.json";
import walkingLottie from "../../images/lottie/walking.json";
import taxiLottie from "../../images/lottie/taxi.json";
import shipLottie from "../../images/lottie/ship.json";
import metroLottie from "../../images/lottie/metro.json";
import busLottie from "../../images/lottie/bus.json";

(maplibregl as any).workerUrl = `${process.env.PUBLIC_URL}/maplibre-gl-csp-worker.js`;

interface MapboxGLProps {
  voyageKey: string;
  selectedCardID: string | null;
  compteurClick: number;
  onMarkerClick: (markerID: string) => void;
  eventCategories: JourneeType[],
  isModif: boolean,
  onDeplacementMarker: (lat1: any, longi1: any, lat2: any, longi2: any, typeEvenement: string, idEvenement: number, idJournee: number) => void;
  placementCardID: string | null;
  onEffacePlacement: () => void; 
}

function MapboxGL({ voyageKey, selectedCardID, compteurClick, onMarkerClick, eventCategories, 
                    isModif, onDeplacementMarker, placementCardID, onEffacePlacement }: MapboxGLProps) {

  const { t } = useTranslation();

  const [zoomTimeout, setZoomTimeout] = useState<NodeJS.Timeout | null>(null);
  const [supercluster, setSupercluster] = useState<Supercluster | null>(null);
  const [isMapLoad, setIsMapLoad] = useState<boolean>(false);
  const [isMajMap, setIsMajMap] = useState<boolean>(false);
  const [zoom, setZoom] = useState<number>(0);
  const mapRef = useRef<any | null>(null);
  const [geojson, setGeojson] = useState<any>({
    type: 'FeatureCollection',
    features: [],
  });
  const [viewport, setViewport] = useState<any>({
    longitude: 0,
    latitude: 0,
    zoom: 99,
    maxZoom: 18
  });
  const [validCoordinates, setValidCoordinates] = useState<number[][]>([]);
  const [visibleClusters, setVisibleClusters] = useState<any[]>([]);
  const [selectedCluster, setSelectedCluster] = useState<any>(null);
  const [petalMarkers, setPetalMarkers] = useState<any[]>([]);
const placementCardIDRef = useRef<string | null>(null);
const touchStartRef = useRef<number | null>(null);
const touchMoveRef = useRef(false);
const lastMarkerTouchRef = useRef(0);
const isDraggingMarkerRef = useRef(false);
const dragStartedAtRef = useRef(0);
const pendingDraggedCoordinateRef = useRef<[number, number] | null>(null);
const pendingDraggedCoordinateTimeoutRef = useRef<number | null>(null);
  const [oldEventCategories, setOldEventCategories] = useState<any>(null);
  const [etapes, setEtapes] = useState<any[]>([]);

  const zoomRef = useRef(zoom);  
  const [isLoading, setIsLoading] = useState(false);

  const [animatedTransport, setAnimatedTransport] = useState<
  Feature<
    GeoJSON.Point,
    {
      icon: string;
      typeTransport?: string;
      bearing?: number;
      goingRight?: boolean;
    }
  > | null
>(null);
const [isAnimating, setIsAnimating] = useState(false);
const [linesGeoJSON, setLinesGeoJSON] = useState<FeatureCollection<LineString>>({
  type: "FeatureCollection",
  features: []
});
const [currentAnimatingLineId, setCurrentAnimatingLineId] = useState<string | null>(null);
const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
const [showGeodesicLine, setShowGeodesicLine] = useState(true);
const [animatedGpsLine, setAnimatedGpsLine] = useState<Feature<LineString> | null>(null);
const [hiddenGeodesicLineId, setHiddenGeodesicLineId] = useState<string | null>(null);
const [isLegendOpen, setIsLegendOpen] = useState(false);
const [showAllMarkerInfos, setShowAllMarkerInfos] = useState(false);
const [openedMarkerInfoIds, setOpenedMarkerInfoIds] = useState<string[]>([]);
const [hoveredMarkerInfoId, setHoveredMarkerInfoId] = useState<string | null>(null);

const [transportLabels, setTransportLabels] = useState<{
  startLabel?: string;
  startCoords?: [number, number];
  endLabel?: string;
  endCoords?: [number, number];
}>({});
type Etape = {
  id: string | number;
  longi: string;
  lat: string;
  index?: number; 
  libelle?: string;
};

const ROUTE_LINE_COLOR = "#334155"; // gris/bleu moderne unique pour tous les traits

const MAP_COLORS = {
  lieu: "#E15265",
  repas: "#E9A23C",
  trajet: "#4DA155",
  nuit: "#3762E3",
  location: "#7540E4",
  cluster: "#EC4899",
};

const toggleMarkerInfo = (id: string) => {
  setOpenedMarkerInfoIds((prev) =>
    prev.includes(id)
      ? prev.filter((currentId) => currentId !== id)
      : [...prev, id]
  );
};

useEffect(() => {
  const map = mapRef.current?.getMap();
  if (!map) return;

 const handleMouseMove = (e: any) => {
  if (!map.getLayer("transport-layer")) {
    map.getCanvas().style.cursor = "";
    return;
  }

  const features = map.queryRenderedFeatures(e.point, {
    layers: ["transport-layer"]
  });

  if (features.length > 0) {
    map.getCanvas().style.cursor = "pointer";
  } else {
    map.getCanvas().style.cursor = "";
  }
};

  map.on("mousemove", handleMouseMove);

  return () => {
    map.off("mousemove", handleMouseMove);
  };
}, [mapRef, isMapLoad]);

useEffect(() => {
  if (validCoordinates.length > 1) {
    const lineCollection = calculateGeodesicLine(validCoordinates);
    setLinesGeoJSON(lineCollection);
  }
}, [validCoordinates]);

useEffect(() => {
  const map = mapRef.current?.getMap();
  if (!map || !isMapLoad || validCoordinates.length === 0) return;

  const coordinates = validCoordinates.filter(
    ([longitude, latitude]) =>
      Number.isFinite(longitude) &&
      Number.isFinite(latitude) &&
      latitude >= -90 &&
      latitude <= 90 &&
      longitude >= -180 &&
      longitude <= 180
  );

  if (coordinates.length === 0) return;

  const pendingDraggedCoordinate = pendingDraggedCoordinateRef.current;

  if (pendingDraggedCoordinate) {
    const [draggedLongitude, draggedLatitude] = pendingDraggedCoordinate;
    const movedMarkerHasBeenUpdated = coordinates.some(
      ([longitude, latitude]) =>
        Math.abs(longitude - draggedLongitude) < 0.000001 &&
        Math.abs(latitude - draggedLatitude) < 0.000001
    );

    if (movedMarkerHasBeenUpdated) {
      pendingDraggedCoordinateRef.current = null;

      if (pendingDraggedCoordinateTimeoutRef.current !== null) {
        window.clearTimeout(pendingDraggedCoordinateTimeoutRef.current);
        pendingDraggedCoordinateTimeoutRef.current = null;
      }
    }

    // Les coordonnées changent après le déplacement du marqueur, mais la
    // caméra doit conserver exactement le cadrage choisi par l'utilisateur.
    return;
  }

  map.resize();

  if (coordinates.length === 1) {
    map.easeTo({
      center: coordinates[0] as [number, number],
      zoom: 12,
      duration: 0,
    });
    return;
  }

  // Ramène les longitudes autour du premier point pour les voyages qui
  // traversent la ligne de changement de date.
  const referenceLongitude = coordinates[0][0];
  const bounds = new maplibregl.LngLatBounds();

  coordinates.forEach(([originalLongitude, latitude]) => {
    let longitude = originalLongitude;

    while (longitude - referenceLongitude > 180) longitude -= 360;
    while (longitude - referenceLongitude < -180) longitude += 360;

    bounds.extend([longitude, latitude]);
  });

  map.fitBounds(bounds, {
    padding: { top: 50, right: 50, bottom: 50, left: 50 },
    maxZoom: 14,
    duration: 0,
  });
}, [validCoordinates, isMapLoad]);

useEffect(() => {
  return () => {
    if (pendingDraggedCoordinateTimeoutRef.current !== null) {
      window.clearTimeout(pendingDraggedCoordinateTimeoutRef.current);
    }
  };
}, []);

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
  const map = mapRef.current?.getMap();

  if (!map || !isMapLoad) return;

  const handleMapClick = () => {
    hidePetalMarkers();
  };

  map.on("click", handleMapClick);

  return () => {
    map.off("click", handleMapClick);
  };
}, [isMapLoad]);


  const handleMarkerClick = (marker: any) => {
    onEffacePlacement();
    onMarkerClick(marker.properties?.id);
  };

  useEffect(() => {
    placementCardIDRef.current = placementCardID;
  }, [placementCardID]);

  
  const determineMarkerImage = (type: string) => {
    switch (type) {
      case 'L':
        return markerRose;
      case 'R':
        return markerJaune;
      case 'T1':
      case 'T2':
        return markerVert;
      case 'L1':
      case 'L2':
        return markerLoc;
      case 'N1':
      case 'N2':
        return markerBleu;
      default:
        return markerRose; // Image par défaut
    }
  };

  const getTransportTheme = (typeTransport?: string) => {
  switch (typeTransport) {
    case "plane":
      return { icon: "las la-plane", color: MAP_COLORS.trajet };
    case "train":
      return { icon: "las la-train", color: MAP_COLORS.trajet };
    case "ship":
      return { icon: "las la-ship", color: MAP_COLORS.trajet };
    case "bus":
      return { icon: "las la-bus", color: MAP_COLORS.trajet };
    case "taxi":
      return { icon: "las la-taxi", color: MAP_COLORS.trajet };
    case "car":
      return { icon: "las la-car", color: MAP_COLORS.trajet };
    case "motorcycle":
      return { icon: "las la-motorcycle", color: MAP_COLORS.trajet };
    case "bicycle":
      return { icon: "las la-bicycle", color: MAP_COLORS.trajet };
    case "walking":
      return { icon: "las la-walking", color: MAP_COLORS.trajet };
    default:
      return { icon: "las la-route", color: MAP_COLORS.trajet };
  }
};
const renderTransportBadges = () => {
  const transportData = calculateLineCenters(etapes, linesGeoJSON);

  return transportData.features.map((feature: any, index: number) => {
    const [longitude, latitude] = feature.geometry.coordinates;
    const typeTransport = feature.properties?.typeTransport;
    const lineId = feature.properties?.lineId;
    if (lineId === hiddenGeodesicLineId) {
  return null;
}
    const icon = feature.properties?.icon;

    const theme = getTransportTheme(typeTransport);

    return (
      <Marker
        key={`transport-badge-${lineId}-${index}`}
        longitude={longitude}
        latitude={latitude}
        anchor="center"
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            restartAnimation(lineId, icon);
          }}
          style={{
            width: "26px",
            height: "26px",
            borderRadius: "999px",
            border: "1px solid green",
            background: "rgba(229, 245, 236, 0.94)",
            boxShadow:
              "0 8px 18px rgba(15,23,42,0.18), 0 0 0 4px rgba(34,197,94,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            backdropFilter: "blur(8px)",
          }}
        >
          <i
            className={`${theme.icon}`}
            style={{
              color: theme.color,
              fontSize: "19px",
              fontWeight: 900,
            }}
          />
        </button>
      </Marker>
    );
  });
};

  const getMap = () => {
    
    let etapesMap : any[] = [];
  
    let i = 0; // Index pour gérer les virgules.
    let oldJour = 0;
    let oldLibelle = '';
    let oldLat = '';
    let oldLong = '';
    let identique = false;
    let numOrdre = 1;

    let numJourAttente=0;
    let libelleAttente="";
    let latAttente="";
    let longiAttente="";
    let typeAttente="";
    let idAttente=0;
    let typeTransportAttente="";

    eventCategories.forEach((journee) => {
      journee.evenements.forEach((evenement) => {
      
      let typeEtape=evenement.typeEv;
      
      if(evenement.typeEv === 'L' || evenement.typeEv === 'N')
      {
            let libelleEtape="";
            let latEtape="";
            let longiEtape="";
            let activitesEtape="";
  
            if (evenement.typeEv === 'L' && evenement.type === 'O') {
              typeEtape = 'R';
            }

            if (evenement.typeEv === 'L' && evenement.lat !== '' && evenement.lat !== null 
            && evenement.libelle1 !== "")
            {
              libelleEtape=evenement.libelle1 || "";
              latEtape=evenement.lat;
              longiEtape=evenement.longi || "";
              activitesEtape=evenement.libelle2 || "";  
            }
            else if (evenement.typeEv === 'N' && evenement.lat !== '' && evenement.lat !== null
                     && (evenement.lat2 === '' || evenement.lat2 === null)
                     && evenement.libelle1 !== "")
            {
              libelleEtape=evenement.libelle1 || "";
              latEtape=evenement.lat;
              longiEtape=evenement.longi || "";
              activitesEtape="";  
              typeEtape = 'N1';
            }
            else if(evenement.typeEv === 'N' && evenement.lat2 !== '' && evenement.lat2 !== null
            && evenement.libelle2 !== "") {
              libelleEtape=evenement.libelle2 || "";
              latEtape=evenement.lat2;
              longiEtape=evenement.longi2 || "";
              activitesEtape="";  
              typeEtape = 'N2';
            }
          
            if(libelleEtape !== '')
            { 
            if (i === 0) {
              if (latEtape !== "") {
                etapesMap.push({
                  numJour: journee.num_jour,
                  libelle: libelleEtape,
                  lat: latEtape,
                  longi: longiEtape,
                  ordre: numOrdre,
                  type: typeEtape,
                  id: evenement.id,
                  activites: activitesEtape,
                  typeTransport: ""
                });
                numOrdre++;
                i++;
                oldLibelle = libelleEtape;
                oldLat = latEtape;
                oldLong = longiEtape;
              }
              oldJour = journee.num_jour;
            } else {
              if (latEtape !== '' && latEtape !== null) {
                if (oldJour === journee.num_jour) {
                  if (
                    oldLibelle === libelleEtape &&
                    oldLat === latEtape &&
                    oldLong === longiEtape
                  ) {
                    identique=true;
                    etapesMap.push({
                        numJour: journee.num_jour,
                        libelle: libelleEtape,
                        lat: latEtape,
                        longi: longiEtape,
                        ordre: numOrdre,
                        type: typeEtape ,
                        id: evenement.id,
                        activites: activitesEtape,
                        typeTransport: ""
                      });
                    numOrdre++;
                    i++;
                    numJourAttente=0;
                    libelleAttente="";
                    latAttente="";
                    longiAttente="";
                    typeAttente="";
                    typeTransportAttente="";
                    idAttente=0;  
                    }
                  }

                  if(!identique)
                  {
                    if(libelleAttente !== "")
                    {
                        etapesMap.push({
                          numJour: numJourAttente,
                          libelle: libelleAttente,
                          lat: latAttente,
                          longi: longiAttente,
                          ordre: numOrdre,
                          type: typeAttente,
                          id: idAttente,
                          activites: "",
                          typeTransport: typeTransportAttente
                        });
                        numOrdre++;
                        i++;

                      numJourAttente=0;
                      libelleAttente="";
                      latAttente="";
                      longiAttente="";
                      typeAttente="";
                      typeTransportAttente="";
                      idAttente=0;
                    }
  
                    etapesMap.push({
                    numJour: journee.num_jour,
                    libelle: libelleEtape,
                    lat: latEtape,
                    longi: longiEtape,
                    ordre: numOrdre,
                    type: typeEtape ,
                    id: evenement.id,
                    activites: activitesEtape,
                    typeTransport: ""
                    });
                    numOrdre++;
                    i++;                  
                  }
            
                  identique=false;
                  oldLibelle = libelleEtape;
                  oldLat = latEtape;
                  oldLong = longiEtape;
                  oldJour = journee.num_jour;
              }
            }
           }
          }
          else
          {
            let libelleEtape1="";
            let latEtape1="";
            let longiEtape1="";
            let typeEtape1="T1";
            let typeTransport1="";
  
            let libelleEtape2="";
            let latEtape2="";
            let longiEtape2="";
            let typeEtape2="T2";
  
            if (
              evenement.typeEv === 'T' &&
              evenement.location === 'O' &&
              evenement.moment === 'P'
            ) {
              typeEtape1 = 'L1';
              typeEtape2 = '';
            } else if (
              evenement.typeEv === 'T' &&
              evenement.location === 'O' &&
              evenement.moment === 'R'
            ) {
              typeEtape1='L2';
              typeEtape2 = '';
            }
            
            if ((typeEtape1 === 'T1' || typeEtape1 === 'L1' || typeEtape1 === 'L2') && evenement.lat !== '' && evenement.lat !== null
              && evenement.libelle1 !== "")
            {
              libelleEtape1=evenement.libelle1 || "";
              latEtape1=evenement.lat;
              longiEtape1=evenement.longi || "";  
              if(typeEtape1 === 'T1')
              {
                typeTransport1=evenement.type || "";
              }
            }
            if (typeEtape2 === 'T2' && evenement.lat2 !== '' && evenement.lat2 !== null
            && evenement.libelle2 !== ""
            && (libelleEtape1 !== evenement.libelle2 || latEtape1 !== evenement.lat2 || longiEtape1 !== evenement.longi2))
            {
              libelleEtape2=evenement.libelle2 || "";
              latEtape2=evenement.lat2;
              longiEtape2=evenement.longi2 || "";  
            }

            if(libelleEtape1 !== '' || libelleEtape2 !== '')
            { 

            if (i === 0) {
              if (latEtape1 !== "" && latEtape1 !== latEtape2) {
                if(latEtape2 !== "")
                {
                  etapesMap.push({
                    numJour: journee.num_jour,
                    libelle: libelleEtape1,
                    lat: latEtape1,
                    longi: longiEtape1,
                    ordre: numOrdre,
                    type: typeEtape1,
                    id: evenement.id,
                    activites: "",
                    typeTransport: typeTransport1
                  });
                  numOrdre++;
                  i++;
                  oldLibelle = libelleEtape1;
                  oldLat = latEtape1;
                  oldLong = longiEtape1;
                }
                else
                {
                  numJourAttente=journee.num_jour;
                  libelleAttente=libelleEtape1;
                  latAttente=latEtape1;
                  longiAttente=longiEtape1;
                  typeAttente=typeEtape1;
                  idAttente=evenement.id;
                  typeTransportAttente=typeTransport1;

                  i++;
                  oldLibelle = libelleEtape1;
                  oldLat = latEtape1;
                  oldLong = longiEtape1;

                }
              }
              if (latEtape2 !== "") {
                
                numJourAttente=journee.num_jour;
                libelleAttente=libelleEtape2;
                latAttente=latEtape2;
                longiAttente=longiEtape2;
                typeAttente=typeEtape2;
                idAttente=evenement.id;
                typeTransportAttente="";

                oldLibelle = libelleEtape2;
                oldLat = latEtape2;
                oldLong = longiEtape2;
              }
              oldJour = journee.num_jour;
            } else {

              if (latEtape1 !== "") {
                if (oldJour === journee.num_jour) {
                  if (
                    oldLibelle === libelleEtape1 &&
                    oldLat === latEtape1 &&
                    oldLong === longiEtape1
                  ) {
                    identique = true;

                    if(i-1 >=0 && etapesMap[i-1] && etapesMap[i-1].typeTransport === '')
                    {
                      etapesMap[i-1].typeTransport=typeTransport1;
                    }
                  typeTransportAttente=typeTransport1;
                  }
                }
                if (!identique) {
                  if(libelleAttente !== "")
                  {
                    etapesMap.push({
                      numJour: numJourAttente,
                      libelle: libelleAttente,
                      lat: latAttente,
                      longi: longiAttente,
                      ordre: numOrdre,
                      type: typeAttente,
                      id: idAttente,
                      activites: "",
                      typeTransport: typeTransportAttente
                    });
                    numOrdre++;
                    i++;
                  }
                  numJourAttente=journee.num_jour;
                  libelleAttente=libelleEtape1;
                  latAttente=latEtape1;
                  longiAttente=longiEtape1;
                  typeAttente=typeEtape1;
                  idAttente=evenement.id;
                  typeTransportAttente=typeTransport1;
                }
                identique = false;
                oldLibelle = libelleEtape1;
                oldLat = latEtape1;
                oldLong = longiEtape1;
                oldJour = journee.num_jour;
              }
              if (latEtape2 !== "") {
                if (oldJour === journee.num_jour) {
                  if (
                    oldLibelle === libelleEtape2 &&
                    oldLat === latEtape2 &&
                    oldLong === longiEtape2
                  ) {
                    identique = true;
                  }
                }
                if (!identique) {
                  if(libelleAttente !== "")
                  {
                    etapesMap.push({
                      numJour: numJourAttente,
                      libelle: libelleAttente,
                      lat: latAttente,
                      longi: longiAttente,
                      ordre: numOrdre,
                      type: typeAttente,
                      id: idAttente,
                      activites: "",
                      typeTransport: typeTransportAttente
                    });
                    numOrdre++;
                    i++;    
                  }
                  numJourAttente=journee.num_jour;
                  libelleAttente=libelleEtape2;
                  latAttente=latEtape2;
                  longiAttente=longiEtape2;
                  typeAttente=typeEtape2;
                  idAttente=evenement.id;
                  typeTransportAttente="";
                }
                identique = false;
                oldLibelle = libelleEtape2;
                oldLat = latEtape2;
                oldLong = longiEtape2;
                oldJour = journee.num_jour;
              } 
              oldJour = journee.num_jour;
            }
           }
          }
        });
    });
  
    if(libelleAttente !== "")
                  {
                    etapesMap.push({
                      numJour: numJourAttente,
                      libelle: libelleAttente,
                      lat: latAttente,
                      longi: longiAttente,
                      ordre: numOrdre,
                      type: typeAttente,
                      id: idAttente,
                      activites: "",
                      typeTransport: typeTransportAttente,
                    });
                    numOrdre++;
                    i++;    
                  }

    return etapesMap;
  };

  useEffect(() => {

  /*  if(JSON.stringify(eventCategories) !== JSON.stringify(oldEventCategories))
    {*/

      const mapEtapes = getMap();
      setEtapes(mapEtapes);

      let sequenceNumber = 1;
      
      const features: GeoJSON.Feature<GeoJSON.Point, any>[] = mapEtapes.map((emplacement: any) => {
        let idPrefix = '';
        if (emplacement.type === "L" || emplacement.type === "R") {
          idPrefix = "lieu-";
        } else if (emplacement.type === "T1" || emplacement.type === "L1" || emplacement.type === "L2") {
          idPrefix = "depart-trajet-";
        } else if (emplacement.type === "T2" ) {
          idPrefix = "arrivee-trajet-";
        } else if (emplacement.type === "N1") {
          idPrefix = "ville-nuit-";
        } else if (emplacement.type === "N2") {
          idPrefix = "hebergement-nuit-";
        }
      
        const markerImage = determineMarkerImage(emplacement.type);
      
        const feature: GeoJSON.Feature<GeoJSON.Point, any> = {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [parseFloat(emplacement.longi), parseFloat(emplacement.lat)]
          },
          properties: {
            id: idPrefix + emplacement.id,
            markerImage: markerImage,
            numJour: emplacement.numJour,
            sequenceNumber: sequenceNumber++,
            libelle: emplacement.libelle,
            type: emplacement.type,
          }
        };
      
        return feature;
      }).filter((feature) => {
        const [longitude, latitude] = feature.geometry.coordinates;

        return Number.isFinite(longitude) &&
          Number.isFinite(latitude) &&
          longitude >= -180 &&
          longitude <= 180 &&
          latitude >= -90 &&
          latitude <= 90;
      });

      setGeojson({
        type: 'FeatureCollection',
        features: features
      });

      const newSupercluster = new Supercluster({
        radius: 1,
        maxZoom: 22
      });
      newSupercluster.load(features);
      setSupercluster(newSupercluster);

      if (features.length > 0) {
        const coordinates = features.map((feature: any) => {
          const [long, lat] = feature.geometry.coordinates;
          return [parseFloat(long), parseFloat(lat)];
        });

        if (coordinates.length === 0) {
          console.error("Invalid coordinates.");
          return;
        }

        setValidCoordinates(coordinates);

        if(viewport.longitude === 0 && viewport.latitude === 0 && viewport.zoom === 99)
        {
          const { longitude, latitude, zoom } = calculateViewport(coordinates);

          setViewport({ longitude, latitude, zoom });
        }
      }
      setIsMajMap(true);

    /*  setOldEventCategories(eventCategories);
    }*/
        
  }, [eventCategories]);

  const zoomToMarker = (coordinates: [number, number]) => {

    var coordLat=coordinates[1];
    var coordLong=coordinates[0];
    if(viewport.latitude === coordLat && viewport.longitude === coordLong)
    {
      coordLong=coordLong+0.00001;
    }

     setViewport({
      ...viewport,
      longitude: coordLong,
      latitude: coordLat,
      zoom: 10
    });

    setZoom(10);
  };

  useEffect(() => {
    onEffacePlacement();
    setPetalMarkers([]);
    if(compteurClick > 0)
    {
      const clickedFeatureId = selectedCardID;

      if(clickedFeatureId)
      {

      const clickedFeature = geojson.features.find((feature: any) => feature.properties.id === clickedFeatureId);
      
      if (clickedFeature) {
        const coordinates = clickedFeature.geometry.coordinates;
        zoomToMarker(coordinates);
        setIsMajMap(true);
      }
      else
      {
        eventCategories.forEach((day) =>
        day.evenements.forEach((evenement) => {

          let newId="";
          let typeId="";

          if(clickedFeatureId.startsWith("arrivee-trajet-"))
          {
            newId=clickedFeatureId.split("arrivee-trajet-")[1];
            typeId="T";
            if (evenement.typeEv === typeId && evenement.id.toString() === newId) {
              zoomToMarker([Number(evenement.longi2), Number(evenement.lat2)]);
              setIsMajMap(true);
            }
          }
          else if(clickedFeatureId.startsWith("depart-trajet-"))
          {
            newId=clickedFeatureId.split("depart-trajet-")[1];
            typeId="T";
            if (evenement.typeEv === typeId && evenement.id.toString() === newId) {
              zoomToMarker([Number(evenement.longi), Number(evenement.lat)]);
              setIsMajMap(true);
            }
          }
          else if(clickedFeatureId.startsWith("ville-hebergement-"))
          {
            newId=clickedFeatureId.split("ville-hebergement-")[1];
            typeId="N";
            if (evenement.typeEv === typeId && evenement.id.toString() === newId) {
              zoomToMarker([Number(evenement.longi2), Number(evenement.lat2)]);
              setIsMajMap(true);
            }
          }
          else if(clickedFeatureId.startsWith("ville-nuit-"))
          {
            newId=clickedFeatureId.split("ville-nuit-")[1];
            typeId="N";
            if (evenement.typeEv === typeId && evenement.id.toString() === newId) {
              zoomToMarker([Number(evenement.longi), Number(evenement.lat)]);
              setIsMajMap(true);
            }
          }

        })
      );
      }
    }
    }
  }, [compteurClick]);

  const calculateViewport = (coordinates: number[][]) => {
   
    let minLongitude = Infinity;
    let maxLongitude = -Infinity;
    let minLatitude = Infinity;
    let maxLatitude = -Infinity;

    coordinates.forEach(([long, lat]) => {
      minLongitude = Math.min(minLongitude, long);
      maxLongitude = Math.max(maxLongitude, long);
      minLatitude = Math.min(minLatitude, lat);
      maxLatitude = Math.max(maxLatitude, lat);
    });

    const longitude = (minLongitude + maxLongitude) / 2;
    const latitude = (minLatitude + maxLatitude) / 2;

    const maxZoom = 18;
    const zoom = Math.min(
      maxZoom,
      Math.floor(Math.log2(360 / (maxLongitude - minLongitude)))
    );

    setZoom(zoom);

    return { longitude, latitude, zoom };
  };

const handleMapLoad = () => {
  setIsMapLoad(true);

  const map = mapRef.current?.getMap();
  if (!map) return;

  const markerImages = [markerRose, markerJaune, markerVert, markerLoc, markerBleu];

  markerImages.forEach((markerImage, index) => {
    const image = new Image();
    image.src = markerImage;

    image.onload = function () {
      if (!map.hasImage(`marker${index}`)) {
        map.addImage(`marker${index}`, image);
      }
    };
  });

  const handleMoveEnd = () => {
    setIsMajMap(true);
  };

  const handleContextMenu = (e: any) => {
    if (isDraggingMarkerRef.current) return;
    const coordinates = e.lngLat;
    placementMarker(coordinates);
  };

  const handleTouchStart = () => {
    if (isDraggingMarkerRef.current) return;
    touchStartRef.current = Date.now();
    touchMoveRef.current = false;
  };

  const handleTouchMove = () => {
    touchMoveRef.current = true;
  };

  const handleTouchEnd = (e: any) => {
    if (isDraggingMarkerRef.current) return;
    if (!placementCardIDRef.current) return;

    const touchStart = touchStartRef.current;
    if (!touchStart) return;

    const duration = Date.now() - touchStart;

    if (duration >= 1000 && !touchMoveRef.current) {
      placementMarker(e.lngLat);
    }

    touchStartRef.current = null;
    touchMoveRef.current = false;
  };

  map.on("moveend", handleMoveEnd);
  map.on("contextmenu", handleContextMenu);
  map.on("touchstart", handleTouchStart);
  map.on("touchmove", handleTouchMove);
  map.on("touchend", handleTouchEnd);

  return () => {
    map.off("moveend", handleMoveEnd);
    map.off("contextmenu", handleContextMenu);
    map.off("touchstart", handleTouchStart);
    map.off("touchmove", handleTouchMove);
    map.off("touchend", handleTouchEnd);
  };
};

const placementMarker = (coordinates: any) => {
  if (isDraggingMarkerRef.current) return;

  const currentPlacementCardID = placementCardIDRef.current;

  if (currentPlacementCardID && currentPlacementCardID !== "") {
    handleMarkerMove(currentPlacementCardID, coordinates);
    onEffacePlacement();
  } else {
    alert(t("mapPlacementAlert"));
  }
};

  useEffect(() => {

    setPetalMarkers([]);

    const updateVisibleClusters = () => {
  
    if (!supercluster || !mapRef.current || !isMapLoad) return;
  
    const map = mapRef.current.getMap();
    if (!map) return;
    
      const bounds = map.getBounds();
      if (!bounds) {
        console.error('Bounds are undefined.');
        return;
      }
      const sw = bounds.getSouthWest(); // Coin sud-ouest
      const ne = bounds.getNorthEast(); // Coin nord-est
      const boundsArray: [number, number, number, number] = [sw.lng, sw.lat, ne.lng, ne.lat]; // Ajouter des valeurs de profondeur arbitraires
      
      const clusters = supercluster.getClusters(boundsArray, zoom);
  
      setVisibleClusters(clusters);

    };

    updateVisibleClusters();
    
    setIsMajMap(false);



  }, [supercluster, zoom, isMapLoad, isMajMap]);

  const majZoom = () => {

    setPetalMarkers([]);

    const updateVisibleClusters = () => {
  
    if (!supercluster || !mapRef.current || !isMapLoad) return;
  
    const map = mapRef.current.getMap();
    if (!map) return;
    
      const bounds = map.getBounds();
      if (!bounds) {
        console.error('Bounds are undefined.');
        return;
      }
      const sw = bounds.getSouthWest(); // Coin sud-ouest
      const ne = bounds.getNorthEast(); // Coin nord-est
      const boundsArray: [number, number, number, number] = [sw.lng, sw.lat, ne.lng, ne.lat]; // Ajouter des valeurs de profondeur arbitraires
      
      const clusters = supercluster.getClusters(boundsArray, zoom);
  
      setVisibleClusters(clusters);

    };

    updateVisibleClusters();
    
    setIsMajMap(false);



  };

const hidePetalMarkers = () => {
  setPetalMarkers([]);
  setSelectedCluster(null);
};

// Fonction pour afficher les marqueurs en pétale autour du cluster sélectionné
const renderPetalMarkers = (cluster: any) => {
  if (selectedCluster !== null && selectedCluster === cluster.id) {
  hidePetalMarkers();
} else {
  setSelectedCluster(cluster.id);
    if (!supercluster) return null;

    const leaves = supercluster.getLeaves(cluster.id, Infinity);

    let distance = 0;

    const map = mapRef.current.getMap();
    if (!map) return;

    let newZoom = map.getZoom();

    switch (Math.round(newZoom)) {
      case 1:
        distance = 15;
        break;
      case 2:
        distance = 6;
        break;
      case 3:
        distance = 3;
        break;
      case 4:
        distance = 1.8;
        break;
      case 5:
        distance = 0.8;
        break;
      case 6:
        distance = 0.4;
        break;
      case 7:
        distance = 0.3;
        break;
      case 8:
        distance = 0.08;
        break;
      case 9:
        distance = 0.05;
        break;
      case 10:
        distance = 0.03;
        break;
      case 11:
        distance = 0.02;
        break;
      case 12:
        distance = 0.01;
        break;
      case 13:
        distance = 0.005;
        break;
      case 14:
        distance = 0.003;
        break;
      case 15:
        distance = 0.001;
        break;
      case 16:
        distance = 0.0006;
        break;
      case 17:
        distance = 0.0004;
        break;
      case 18:
        distance = 0.0002;
        break;
      case 19:
        distance = 0.00006;
        break;
      case 20:
        distance = 0.00008;
        break;
      case 21:
        distance = 0.00004;
        break;
      case 22:
        distance = 0.00002;
        break;
      case 23:
        distance = 0.00001;
        break;
      default:
        distance = 0.1;
    }

    const numMarkers = leaves.length;
    const angleIncrement = (2 * Math.PI) / numMarkers; // Angle entre chaque marqueur

    const petalMarkersCalc = leaves
      .map((marker: Feature, index: number) => {
        if (marker.properties !== null) {
          // Calcul de l'angle pour le marqueur actuel
          const angle = angleIncrement * index;

          // Calcul des coordonnées polaires pour le marqueur
          const markerPolarX = distance * Math.cos(angle);
          const markerPolarY = distance * Math.sin(angle);

          // Conversion des coordonnées polaires en coordonnées cartésiennes en ajoutant les coordonnées du cluster
          const markerLongitude = cluster.geometry.coordinates[0] + markerPolarX;
          const markerLatitude = cluster.geometry.coordinates[1] + markerPolarY;

          const sequenceNumber = marker.properties.sequenceNumber;
          const markerSize = 30; // Taille des marqueurs en pétale
          const markerImage = marker.properties.markerImage; // Image du marqueur en pétale

       return renderMarker(
  marker.properties.numJour,
  marker.properties.libelle,
  marker.properties.id,
  markerLongitude,
  markerLatitude,
  markerImage,
  sequenceNumber,
  () => handleMarkerClick(marker),
  markerSize,
  0,
  marker.properties.type || "L",
  true,
  true
);
        }
        return null;
      })
      .filter(Boolean); // Supprime les valeurs nulles

    setPetalMarkers(petalMarkersCalc);
  }
};

const getTransportLottieConfig = (typeTransport?: string) => {
  switch (typeTransport) {
    case "plane":
      return {
        animation: planeLottie,
        naturalDirection: "right",
        scale: 1,
        offsetX: 0,
        offsetY: 0,
      };

    case "car":
      return {
        animation: carLottie,
        naturalDirection: "left",
        scale: 0.95,
        offsetX: 0,
        offsetY: -10,
      };

    case "metro":
      return {
        animation: metroLottie,
        naturalDirection: "left",
        scale: 1.15,
        offsetX: 0,
        offsetY: 0,
      };

    case "taxi":
      return {
        animation: taxiLottie,
        naturalDirection: "left",
        scale: 0.65,
        offsetX: 0,
        offsetY: -15,
      };

    case "bicycle":
      return {
        animation: bicycleLottie,
        naturalDirection: "right",
        scale: 0.5,
        offsetX: 0,
        offsetY: -20,
      };

    case "motorcycle":
      return {
        animation: motorbikeLottie,
        naturalDirection: "right",
        scale: 0.7,
        offsetX: 0,
        offsetY: -21,
      };

    case "train":
      return {
        animation: trainLottie,
        naturalDirection: "left",
        scale: 0.6,
        offsetX: 0,
        offsetY: -25,
      };

    case "walking":
      return {
        animation: walkingLottie,
        naturalDirection: "left",
        scale: 0.6,
        offsetX: 0,
        offsetY: -25,
      };

    case "ship":
      return {
        animation: shipLottie,
        naturalDirection: "right",
        scale: 0.45,
        offsetX: 0,
        offsetY: -20,
      };

    case "bus":
      return {
        animation: busLottie,
        naturalDirection: "right",
        scale: 0.70,
        offsetX: 0,
        offsetY: -10,
      };

    default:
      return {
        animation: planeLottie,
        naturalDirection: "right",
        scale: 1,
        offsetX: 0,
        offsetY: 0,
      };
  }
};

const handleMarkerMove = (id: string, newPosition: LngLatLike) => {

  onEffacePlacement();
  const { lat, lng } = newPosition as { lat: number, lng: number };

  eventCategories.forEach((journee) => {
    journee.evenements.forEach((evenement) => {
      let newId="";
          let typeId="";
          let latConserv='';
          let longiConserv='';

          if(id.startsWith("lieu-"))
          {
            newId=id.split("lieu-")[1];
            typeId="L";
            if (evenement.typeEv === typeId && evenement.id.toString() === newId) {
              onDeplacementMarker(lat, lng, '', '', 'lieu', Number(newId), journee.id_journee);
            }
          }
          else if(id.startsWith("hebergement-nuit-"))
          {
            newId=id.split("hebergement-nuit-")[1];
            typeId="N";
            
            if (evenement.typeEv === typeId && evenement.id.toString() === newId) {
              if(evenement.lat !== null && evenement.longi !== null)
              {
                latConserv=evenement.lat;
                longiConserv=evenement.longi;
              }
              onDeplacementMarker(latConserv, longiConserv, lat, lng, 'nuit', Number(newId), journee.id_journee);
            }
          }
          else if(id.startsWith("ville-nuit-"))
          {
            newId=id.split("ville-nuit-")[1];
            typeId="N";
            
            if (evenement.typeEv === typeId && evenement.id.toString() === newId) {
              if(evenement.lat2 !== null && evenement.longi2 !== null)
              {
                latConserv=evenement.lat2;
                longiConserv=evenement.longi2;
              }
              onDeplacementMarker( lat, lng, latConserv, longiConserv, 'nuit', Number(newId), journee.id_journee);
            }
          }
          else if(id.startsWith("arrivee-trajet-"))
          {
            newId=id.split("arrivee-trajet-")[1];
            typeId="T";
            
            if (evenement.typeEv === typeId && evenement.id.toString() === newId) {
              if(evenement.lat !== null && evenement.longi !== null)
              {
                latConserv=evenement.lat;
                longiConserv=evenement.longi;
              }
              onDeplacementMarker(latConserv, longiConserv, lat, lng, 'trajet', Number(newId), journee.id_journee);
            }
          }
          else if(id.startsWith("depart-trajet-"))
          {
            newId=id.split("depart-trajet-")[1];
            typeId="T";
            
            if (evenement.typeEv === typeId && evenement.id.toString() === newId) {
              if(evenement.lat2 !== null && evenement.longi2 !== null)
              {
                latConserv=evenement.lat2;
                longiConserv=evenement.longi2;
              }
              onDeplacementMarker( lat, lng, latConserv, longiConserv, 'trajet', Number(newId), journee.id_journee);
            }
          }
    });
  });

};

const getLineBearing = (start: [number, number], end: [number, number]) => {
  const [lon1, lat1] = start.map(deg => deg * Math.PI / 180);
  const [lon2, lat2] = end.map(deg => deg * Math.PI / 180);

  const y = Math.sin(lon2 - lon1) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1);
  const bearing = Math.atan2(y, x) * 180 / Math.PI;
  return (bearing + 360) % 360; // normalisé 0-360°
};

const handleZoom = (e: any) => {
  const zoomLevel = e.viewState.zoom;

  if (Number.isInteger(zoomLevel)) {
    setZoom(zoomLevel);
    if (zoomTimeout) {
      clearTimeout(zoomTimeout);
      setZoomTimeout(null);
    }
  } else {
    if (zoomTimeout) {
      clearTimeout(zoomTimeout);
    }
    const newTimeout = setTimeout(() => {
      setZoom(Math.round(zoomLevel));
      setZoomTimeout(null);
    }, 300);
    setZoomTimeout(newTimeout);
  }
};

useEffect(() => {
  return () => {
    // Cleanup timeout on unmount
    if (zoomTimeout) {
      clearTimeout(zoomTimeout);
    }
  };
}, [zoomTimeout]);
const isGoingRight = (
  start: [number, number],
  end: [number, number],
  map: any
) => {
  const realMap = map as mapboxgl.Map;
  if (!realMap.project) return true; // fallback

  const startPixel = realMap.project({ lng: start[0], lat: start[1] });
  const endPixel = realMap.project({ lng: end[0], lat: end[1] });

  // Vérifier si le trajet est “intercontinental” via les longitudes
  const deltaLng = end[0] - start[0];
  const isIntercontinental = Math.abs(deltaLng) > 180;

  if (isIntercontinental) {
    // Inverser la comparaison écran pour ce cas
    return endPixel.x < startPixel.x;
  }

  // Cas normal
  return endPixel.x > startPixel.x;
};


const animateTransportAlongLineWithGps = async (
  line: Feature<LineString>,
  startEtape: Etape,
  endEtape: Etape,
  typeTransport: string
) => {
  if (!startEtape || !endEtape) return;

  let loadingTimeout: NodeJS.Timeout | null = null;

  try {
  // Définir un timeout de 3 secondes
  loadingTimeout = setTimeout(() => setIsLoading(true), 1000);

  const gpsRoute = await fetchGpsRoute(
    [parseFloat(startEtape.longi), parseFloat(startEtape.lat)],
    [parseFloat(endEtape.longi), parseFloat(endEtape.lat)],
    typeTransport
  );

  // La requête est terminée, on annule le timeout
  if (loadingTimeout) clearTimeout(loadingTimeout);
  setIsLoading(false);

  if (!gpsRoute) {

          setHiddenGeodesicLineId(null);
          animateTransportAlongLine(line, typeTransport, true);
    return;
  }



  setShowGeodesicLine(false);

  let lineToAnimate: Feature<LineString> | null = null;


  if (gpsRoute.geometry.type === "MultiLineString") {
    const mergedCoords = gpsRoute.geometry.coordinates.flat();
    if (mergedCoords.length < 2) return;
    lineToAnimate = {
      type: "Feature",
      properties: gpsRoute.properties,
      geometry: { type: "LineString", coordinates: mergedCoords }
    };
  } else if (gpsRoute.geometry.type === "LineString") {
    lineToAnimate = gpsRoute as Feature<LineString>;
  } else return;

  // Affiche le tracé GPS
  setAnimatedGpsLine(lineToAnimate);

  // ⚡ Lancer l’animation
  animateTransportAlongLine(lineToAnimate, typeTransport, true);
 } catch (error) {
  if (loadingTimeout) clearTimeout(loadingTimeout);
  setIsLoading(false);
  setHiddenGeodesicLineId(null);
  console.error("Erreur fetchGpsRoute :", error);
}

};




const animateTransportAlongLine = (line: Feature<LineString>, typeTransport: string,
  isGpsAnimation = false  ) => {

    const map = mapRef.current?.getMap();
  if (!map) return; // Si la map n'est pas encore prête, sortir

   if (!line.geometry.coordinates || line.geometry.coordinates.length < 2) {
    console.warn("Impossible d’animer : coordonnées insuffisantes", line);

    return;
  }

let coordsFlat: [number, number][];

if (line.geometry.type === "LineString") {
  coordsFlat = (line.geometry.coordinates as unknown) as [number, number][];
} else if (line.geometry.type === "MultiLineString") {
  // aplatir toutes les lignes du MultiLineString
  coordsFlat = ((line.geometry.coordinates as unknown) as [number, number][][]).flat();
} else {
  console.error("Géométrie non supportée :", (line.geometry as any).type);
  return;
}


  const lineString = turf.lineString(line.geometry.coordinates, line.properties);

  if (!lineString.geometry.coordinates || lineString.geometry.coordinates.length < 2) {
    console.error("Line invalide pour turf.along :", lineString);
    setIsAnimating(false);
    return;
  }

  const lineLength = turf.length(lineString, { units: 'kilometers' });
  if (lineLength === 0) {
    setIsAnimating(false);
    return;
  }

  const duration = 6000; // Durée totale de l'animation (ms)
  const startTime = performance.now();

  const animateStep = (time: number) => {

    
    const elapsed = time - startTime;
    let progress = elapsed / duration;

    // Limiter progress à 1
    if (progress > 1) progress = 1;

    const distanceAlongLine = Math.min(lineLength, Math.max(0, lineLength * progress));

    const currentPoint = turf.along(lineString, distanceAlongLine, { units: 'kilometers' });
    if (!currentPoint) {
      console.warn('currentPoint est undefined, skipping update');
      setIsAnimating(false);
      return;
    }

    const start = line.geometry.coordinates[0] as [number, number];
const end = line.geometry.coordinates[line.geometry.coordinates.length - 1] as [number, number];

const goingRight = isGoingRight(start, end, map);

const iconName = getTransportIcon(line.properties?.typeTransport ?? typeTransport, goingRight);

const before = turf.along(
  lineString,
  Math.max(0, distanceAlongLine - lineLength * 0.01),
  { units: "kilometers" }
);

const after = turf.along(
  lineString,
  Math.min(lineLength, distanceAlongLine + lineLength * 0.01),
  { units: "kilometers" }
);

const bearing = turf.bearing(before, after);

setAnimatedTransport({
  type: "Feature",
  geometry: currentPoint.geometry,
  properties: {
    icon: iconName,
    typeTransport,
    bearing,
    goingRight,
  },
} as any);

    if (progress < 1) {
  requestAnimationFrame(animateStep);
} else {
  setAnimatedTransport(null);
  setIsAnimating(false);
  
        // Réinitialiser seulement si ce n'est pas une animation GPS segmentée
        setCurrentAnimatingLineId(null);
           setAnimatedGpsLine(null);
           setHiddenGeodesicLineId(null);
        setTransportLabels({});

}
  };

  setIsAnimating(true);
  requestAnimationFrame(animateStep);

};

const getMarkerTheme = (type?: string) => {
  switch (type) {
    case "R":
      return {
        color: MAP_COLORS.repas,
        bg: "#FFF7ED",
        icon: "las la-utensils",
        iconBg: "#FFF1D6",
        iconColor: MAP_COLORS.repas,
      };

    case "T1":
    case "T2":
      return {
        color: MAP_COLORS.trajet,
        bg: "#ECFDF5",
        icon: "las la-route",
        iconBg: "#DCFCE7",
        iconColor: MAP_COLORS.trajet,
      };

    case "L1":
    case "L2":
      return {
        color: MAP_COLORS.location,
        bg: "#F5F3FF",
        icon: "las la-car",
        iconBg: "#EDE9FE",
        iconColor: MAP_COLORS.location,
      };

    case "N1":
    case "N2":
      return {
        color: MAP_COLORS.nuit,
        bg: "#EFF6FF",
        icon: "las la-bed",
        iconBg: "#DBEAFE",
        iconColor: MAP_COLORS.nuit,
      };

    case "L":
    default:
      return {
        color: MAP_COLORS.lieu,
        bg: "#FFF1F2",
        icon: "las la-map-marker-alt",
        iconBg: "#FFE4EC",
        iconColor: MAP_COLORS.lieu,
      };
  }
};

const renderMarker = (
  numJour: any,
  libelle: any,
  id: any,
  longitude: any,
  latitude: any,
  markerImage: any,
  sequenceNumber: any,
  onClick: any,
  markerSize: any,
  position: any,
  type?: string,
  forceShowInfo: boolean = false,
  hidePicot: boolean = false
) => {
  const theme = getMarkerTheme(type || "L");
  const markerId = String(id);

  const isInfoVisible =
    forceShowInfo ||
    showAllMarkerInfos ||
    openedMarkerInfoIds.includes(markerId) ||
    hoveredMarkerInfoId === markerId;

  return (
    <Marker
      key={id}
      longitude={longitude}
      latitude={latitude}
      anchor="bottom"
      offset={[0, 0]}
      draggable={isModif}
      onDragStart={() => {
        isDraggingMarkerRef.current = true;
        dragStartedAtRef.current = Date.now();
        touchMoveRef.current = true;
      }}
      onDrag={() => {
        isDraggingMarkerRef.current = true;
        touchMoveRef.current = true;
      }}
      onDragEnd={(event) => {
        if (isModif) {
          pendingDraggedCoordinateRef.current = [
            event.lngLat.lng,
            event.lngLat.lat,
          ];

          if (pendingDraggedCoordinateTimeoutRef.current !== null) {
            window.clearTimeout(pendingDraggedCoordinateTimeoutRef.current);
          }

          pendingDraggedCoordinateTimeoutRef.current = window.setTimeout(() => {
            pendingDraggedCoordinateRef.current = null;
            pendingDraggedCoordinateTimeoutRef.current = null;
          }, 30000);

          handleMarkerMove(id, event.lngLat);
        }

        lastMarkerTouchRef.current = Date.now();

        window.setTimeout(() => {
          isDraggingMarkerRef.current = false;
          touchStartRef.current = null;
          touchMoveRef.current = false;
        }, 80);
      }}
      style={{
        zIndex: isInfoVisible ? 9999 : 1,
      }}
    >
      <div
        onMouseEnter={() => {
          if (!window.matchMedia("(hover: none), (pointer: coarse)").matches) {
            setHoveredMarkerInfoId(markerId);
          }
        }}
        onMouseLeave={() => {
          if (!window.matchMedia("(hover: none), (pointer: coarse)").matches) {
            setHoveredMarkerInfoId((currentId) =>
              currentId === markerId ? null : currentId
            );
          }
        }}
        onTouchEnd={(e) => {
          if (isDraggingMarkerRef.current) return;
          if (Date.now() - dragStartedAtRef.current < 250) return;

          e.preventDefault();
          e.stopPropagation();

          lastMarkerTouchRef.current = Date.now();
          setHoveredMarkerInfoId(null);
          onEffacePlacement();

          if (id) {
            onMarkerClick(String(id));
          }

          toggleMarkerInfo(markerId);
        }}
        onClick={(e) => {
          e.stopPropagation();

          if (isDraggingMarkerRef.current) return;
          if (Date.now() - lastMarkerTouchRef.current < 400) return;

          onEffacePlacement();

          if (id) {
            onMarkerClick(String(id));
          }

          toggleMarkerInfo(markerId);
        }}
        style={{
          position: "relative",
          width: isInfoVisible ? "fit-content" : "34px",
          minWidth: isInfoVisible ? "160px" : "34px",
          maxWidth: "280px",
          height: "58px",
          cursor: isModif ? "grab" : "pointer",
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",
          zIndex: isInfoVisible ? 9999 : 1,
          touchAction: "none",
        }}
      >
        {isInfoVisible && (
          <div
            style={{
              position: "absolute",
              left: "70px",
              bottom: "7px",
              height: "28px",
              minWidth: "80px",
              maxWidth: "210px",
              borderRadius: "10px",
              background: "rgba(255,255,255,0.97)",
              border: "1px solid rgba(226,232,240,0.95)",
              boxShadow: "0 8px 20px rgba(15,23,42,0.14)",
              display: "flex",
              alignItems: "center",
              padding: "6px 10px 6px 27px",
              backdropFilter: "blur(8px)",
              zIndex: 1,
              whiteSpace: "nowrap",
            }}
          >
            <div style={{ minWidth: 0, lineHeight: 0.8, textAlign: "left" }}>
              <div
                style={{
                  maxWidth: "180px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: "#0F172A",
                  fontSize: "11px",
                  fontWeight: 900,
                  letterSpacing: "0.01em",
                }}
              >
                {libelle}
              </div>
              <div
                style={{
                  marginTop: "3px",
                  color: "#64748B",
                  fontSize: "9px",
                  fontWeight: 700,
                }}
              >
                {t("jour")} {numJour}
              </div>
            </div>
          </div>
        )}

        <div
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            bottom: "8px",
            width: "26px",
            height: "26px",
            borderRadius: "17px",
            background: theme.color,
            border: "3px solid white",
            boxShadow: `0 8px 18px ${theme.color}55, 0 2px 8px rgba(15,23,42,0.16)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontSize: "12px",
            fontWeight: 900,
            lineHeight: 1,
            zIndex: 3,
          }}
        >
          {sequenceNumber}
        </div>

        {!hidePicot && (
          <div
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%) rotate(45deg)",
              bottom: "3px",
              width: "14px",
              height: "14px",
              background: theme.color,
              borderRight: "3px solid white",
              borderBottom: "3px solid white",
              borderRadius: "3px",
              zIndex: 2,
              boxShadow: `4px 6px 12px ${theme.color}35`,
            }}
          />
        )}
      </div>
    </Marker>
  );
};
  
const formatClusterDays = (leaves: any[]) => {
  const days = Array.from(
    new Set(
      leaves
        .map((leaf) => Number(leaf.properties?.numJour))
        .filter((day) => !Number.isNaN(day))
    )
  ).sort((a, b) => a - b);

  if (days.length === 0) return "";

  if (days.length === 1) {
    return `{${t("jour")} ${days[0]}`;
  }

  const areConsecutive = days.every((day, index) => {
    if (index === 0) return true;
    return day === days[index - 1] + 1;
  });

  if (areConsecutive) {
    return `${t("joursMajuscule")}  ${days[0]} à ${days[days.length - 1]}`;
  }

  return `${t("joursMajuscule")}  ${days.join(", ")}`;
};
  
const renderClusters = () => {
  return (
    <>
      {visibleClusters.map(cluster => {
        const [longitude, latitude] = cluster.geometry.coordinates;
        const { cluster: isCluster, point_count: pointCount } = cluster.properties;
if (isCluster) {
let leaves: any[] = [];

try {
  if (
    supercluster &&
    cluster.id !== undefined &&
    cluster.id !== null
  ) {
    leaves = supercluster.getLeaves(cluster.id, Infinity);
  }
} catch (error) {
  console.warn("Cluster obsolète ignoré :", cluster.id, error);
  leaves = [];
}

const clusterDaysLabel = formatClusterDays(leaves);

const clusterLabel =
  leaves.length > 0 && leaves[0].properties?.libelle
    ? leaves[0].properties.libelle
    : `${pointCount} ${t("lieuxMinuscule")}`;

  return (
    <Marker
  key={`cluster-${cluster.id}`}
  longitude={longitude}
  latitude={latitude}
  anchor="bottom-left"
  offset={[-23, 6]}
>
      <div
  onClick={(e) => {
    e.stopPropagation();
    renderPetalMarkers(cluster);
  }}
  style={{
  position: "relative",
  width: showAllMarkerInfos ? "fit-content" : "34px",
  minWidth: showAllMarkerInfos ? "150px" : "34px",
  maxWidth: "260px",
  height: "58px",
  cursor: "pointer",
  userSelect: "none",
}}
>

  {/* PASTILLE CLUSTER — toujours visible */}
<div
  style={{
    position: "absolute",
    left: "8px",
    bottom: "12px",
    width: "26px",
    height: "26px",
    borderRadius: "17px",
    background: MAP_COLORS.cluster,
    border: "3px solid white",
    boxShadow: `0 8px 18px ${MAP_COLORS.cluster}55, 0 2px 8px rgba(15,23,42,0.16)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontSize: "12px",
    fontWeight: 900,
    lineHeight: 1,
    zIndex: 3,
  }}
>
  {/*pointCount*/}
  <i className="las la-layer-group text-lg"></i>
</div>

{/* PICOT DU CLUSTER — toujours visible */}
<div
  style={{
    position: "absolute",
    left: "13px",
    bottom: "8px",
    width: "16px",
    height: "16px",
    background: MAP_COLORS.cluster,
    borderRight: "3px solid white",
    borderBottom: "3px solid white",
    transform: "rotate(45deg)",
    borderRadius: "3px",
    zIndex: 2,
    boxShadow: `4px 6px 12px ${MAP_COLORS.cluster}35`,
  }}
/>
  
  {/* CARTOUCHE BLANC DU CLUSTER — visible seulement avec "Montrer les infos" */}
{showAllMarkerInfos && (
  <div
    style={{
      position: "absolute",
      left: "10px",
      bottom: "12px",
      height: "28px",
      minWidth: "80px",
      maxWidth: "210px",
      borderRadius: "10px",
      background: "rgba(255,255,255,0.97)",
      border: "1px solid rgba(226,232,240,0.95)",
      boxShadow: "0 8px 20px rgba(15,23,42,0.14)",
      display: "flex",
      alignItems: "center",
      padding: "6px 10px 6px 30px",
      backdropFilter: "blur(8px)",
      zIndex: 1,
      whiteSpace: "nowrap",
    }}
  >
         
         <div
  style={{
    minWidth: 0,
    lineHeight: 0.9,
    textAlign: "left",
  }}
>
  <div
    style={{
      maxWidth: "170px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: "#0F172A",
      fontSize: "11px",
      fontWeight: 900,
      letterSpacing: "0.01em",
    }}
  >
    {clusterLabel}
  </div>

  {clusterDaysLabel && (
    <div
      style={{
        marginTop: "3px",
        color: "#64748B",
        fontSize: "9px",
        fontWeight: 700,
      }}
    >
      {clusterDaysLabel}
    </div>
  )}
</div>
        </div>
      )}
      </div>
    </Marker>
  );
}else {
          const sequenceNumber = cluster.properties.sequenceNumber;
          const markerSize = 30;

          const markerImage = cluster.properties.markerImage;

          return renderMarker(
  cluster.properties.numJour,
  cluster.properties.libelle,
  cluster.properties.id,
  longitude,
  latitude,
  markerImage,
  sequenceNumber,
  () => handleMarkerClick(cluster),
  markerSize,
  -markerSize / 2,
  cluster.properties.type || "L"
);
       
        }
      })}
      {petalMarkers}
    </>
  );
};

const createWavyLine = (
  start: [number, number],
  end: [number, number],
  index: number,
  steps = 48
): [number, number][] => {
  const [x1, y1] = start;
  let [x2, y2] = end;

  /**
   * Important :
   * On force le tracé à prendre le chemin le plus court en longitude.
   * Exemple :
   * San Francisco -122 -> Tokyo 139
   * Sans correction : delta = 261 degrés => Atlantique / Europe
   * Avec correction : Tokyo devient -221 => delta = -99 degrés => Pacifique
   */
  let deltaLng = x2 - x1;

  if (deltaLng > 180) {
    x2 -= 360;
  } else if (deltaLng < -180) {
    x2 += 360;
  }

  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.sqrt(dx * dx + dy * dy) || 1;

  const nx = -dy / length;
  const ny = dx / length;

  const direction = index % 2 === 0 ? 1 : -1;

  const curveStrength = Math.min(length * 0.1, 0.8) * direction;

  const coords: [number, number][] = [];

  for (let i = 0; i <= steps; i++) {
    const t = i / steps;

    const x = x1 + dx * t;
    const y = y1 + dy * t;

    const mainCurve = Math.sin(Math.PI * t) * curveStrength;
    const smallWave = Math.sin(Math.PI * 3 * t) * curveStrength * 0.18;

    const offset = mainCurve + smallWave;

    coords.push([
      x + nx * offset,
      y + ny * offset,
    ]);
  }

  return coords;
};

const calculateGeodesicLine = (
  coordinates: number[][]
): FeatureCollection<LineString, Properties> => {
  const lineFeatures: Feature<LineString, Properties>[] = [];

  for (let i = 0; i < coordinates.length - 1; i++) {
    const start = coordinates[i] as [number, number];
    const end = coordinates[i + 1] as [number, number];

    const line: Feature<LineString, Properties> = {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: createWavyLine(start, end, i),
      },
      properties: {
        id: `line-${i}`,
        startIdx: i,
        endIdx: i + 1,
      },
    };

    lineFeatures.push(line);
  }

  return {
    type: "FeatureCollection",
    features: lineFeatures,
  };
};

const renderRouteArrows = () => {
  if (!linesGeoJSON || !linesGeoJSON.features.length) return null;

  return linesGeoJSON.features.flatMap((line, lineIndex) => {
        if (line.properties?.id === hiddenGeodesicLineId) {
      return [];
    }
    if (!line.geometry.coordinates || line.geometry.coordinates.length < 2) {
      return [];
    }

    const lineString = turf.lineString(line.geometry.coordinates);
    const lineLength = turf.length(lineString, { units: "kilometers" });

    if (!lineLength || lineLength <= 0) return [];

    // Nombre de flèches par segment
    const arrowCount = Math.max(
      1,
      Math.min(3, Math.floor(lineLength / 500))
    );

    return Array.from({ length: arrowCount }).map((_, arrowIndex) => {
      const distance = lineLength * ((arrowIndex + 1) / (arrowCount + 1));

      const point = turf.along(lineString, distance, {
        units: "kilometers",
      });

      const delta = Math.max(lineLength * 0.015, 0.5);

      const before = turf.along(
        lineString,
        Math.max(0, distance - delta),
        { units: "kilometers" }
      );

      const after = turf.along(
        lineString,
        Math.min(lineLength, distance + delta),
        { units: "kilometers" }
      );

      const bearing = turf.bearing(before, after);

      const [longitude, latitude] = point.geometry.coordinates;

      return (
        <Marker
          key={`route-arrow-${lineIndex}-${arrowIndex}`}
          longitude={longitude}
          latitude={latitude}
          anchor="center"
        >
          <div
            style={{
              width: "18px",
              height: "18px",
              pointerEvents: "none",
              transform: `rotate(${bearing - 90}deg)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              filter: "drop-shadow(0 1px 2px rgba(255,255,255,0.95)) drop-shadow(0 1px 3px rgba(15,23,42,0.25))",
              zIndex: 2,
            }}
          >
            <div
              style={{
                width: 0,
                height: 0,
                borderTop: "4px solid transparent",
borderBottom: "4px solid transparent",
borderLeft: `7px solid ${ROUTE_LINE_COLOR}`,
              }}
            />
          </div>
        </Marker>
      );
    });
  });
};

    const getTransportIcon = (typeTransport: string, goingRight: boolean) => {
    switch (typeTransport) {
     case "walking": return goingRight ? "walking-icon-right" : "walking-icon-left";
    case "plane": return goingRight ? "plane-icon-right" : "plane-icon-left";
    case "train": return goingRight ? "train-icon-right" : "train-icon-left";
     case "ship": return goingRight ? "ship-icon-right" : "ship-icon-left";
    case "bus": return goingRight ? "bus-icon-right" : "bus-icon-left";
    case "taxi": return goingRight ? "taxi-icon-right" : "taxi-icon-left";
     case "car": return goingRight ? "car-icon-right" : "car-icon-left";
    case "motorcycle": return goingRight ? "motorcycle-icon-right" : "motorcycle-icon-left";
    case "bicycle": return goingRight ? "bicycle-icon-right" : "bicycle-icon-left";
    default: return goingRight ? "car-icon-right" : "car-icon-left";
  }
  };

const calculateLineCenters = (
  etapes: any[],
  routeLines: FeatureCollection<LineString>
): FeatureCollection => {
  const centerPoints: Feature[] = [];
  const map = mapRef.current?.getMap();

  if (!map || !etapes || !routeLines?.features?.length) {
    return { type: "FeatureCollection", features: [] };
  }

  routeLines.features.forEach((line, i) => {
    if (!line.geometry.coordinates || line.geometry.coordinates.length < 2) return;

    const etape = etapes[i];
    if (!etape?.typeTransport) return;

    const lineString = turf.lineString(line.geometry.coordinates);
    const lineLength = turf.length(lineString, { units: "kilometers" });

    if (!lineLength || lineLength <= 0) return;

    const midpoint = turf.along(lineString, lineLength / 2, {
      units: "kilometers",
    });

    const before = turf.along(lineString, lineLength * 0.48, {
      units: "kilometers",
    });

    const after = turf.along(lineString, lineLength * 0.52, {
      units: "kilometers",
    });

    const bearing = turf.bearing(before, after);

    const startCoords = line.geometry.coordinates[0] as [number, number];
    const endCoords = line.geometry.coordinates[
      line.geometry.coordinates.length - 1
    ] as [number, number];

    const goingRight = isGoingRight(startCoords, endCoords, map);

    const iconName = getTransportIcon(etape.typeTransport, goingRight);

    midpoint.properties = {
      icon: iconName,
      lineId: `line-${i}`,
      bearing,
      typeTransport: etape.typeTransport,
    };

    centerPoints.push(midpoint);
  });

  return {
    type: "FeatureCollection",
    features: centerPoints,
  };
};

const fetchGpsRoute = async (start: [number, number], end: [number, number], mode: string) => {
  const modeMap: Record<string, string> = {
    car: 'drive',
    taxi: 'drive',
    bus: 'bus',
    motorcycle: 'motorcycle',
    bicycle: 'bicycle',
    walking: 'walk',
  };

  const routingMode = modeMap[mode] || 'drive';
  const url = `https://api.geoapify.com/v1/routing?waypoints=${start[1]},${start[0]}|${end[1]},${end[0]}&mode=${routingMode}&apiKey=${apiKeyGeoapify}`;

  try {
    const response = await fetch(url);
    const data = await response.json();

    if (data.features && data.features.length > 0) {
      // Retourne un Feature<LineString>
      return data.features[0];
    } else {
      console.log('Aucun itinéraire trouvé');
      return null;
    }
  } catch (err) {
    console.log('Erreur API Geoapify Routing:', err);
    return null;
  }
};

const normalizeAngle = (angle: number) => {
  let normalized = angle;

  while (normalized > 180) normalized -= 360;
  while (normalized < -180) normalized += 360;

  return normalized;
};

const renderTransportAnimation = () => {
  if (!animatedTransport) return null;

  const [longitude, latitude] = animatedTransport.geometry.coordinates;

  const typeTransport =
    animatedTransport.properties?.typeTransport ||
    animatedTransport.properties?.icon?.split("-")[0] ||
    "car";

  const lottieConfig = getTransportLottieConfig(typeTransport);
const lottieAnimation = lottieConfig.animation;

  const bearing = animatedTransport.properties?.bearing || 0;
  const goingRight = animatedTransport.properties?.goingRight ?? true;

  const shouldFlip =
  (goingRight && lottieConfig.naturalDirection === "left") ||
  (!goingRight && lottieConfig.naturalDirection === "right");

  /**
   * Si l'avion va à droite, son orientation naturelle correspond à l'est, donc 90°.
   * Si l'avion va à gauche, on le retourne avec scaleX(-1), donc son orientation naturelle devient l'ouest, 270°.
   */
  const baseBearing = goingRight ? 90 : 270;

  /**
   * On garde seulement une petite inclinaison pour suivre la pente du trajet,
   * sans retourner l'avion tête en bas.
   */
  const rawTilt = normalizeAngle(bearing - baseBearing);

  const tilt = Math.max(-35, Math.min(35, rawTilt));

  return (
    <Marker longitude={longitude} latitude={latitude} anchor="center">
      <div
        style={{
          position: "relative",
          width: "110px",
          height: "110px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pointerEvents: "none",
          transform: `rotate(${tilt}deg)`,
          transformOrigin: "center center",
        }}
      >
        <div
  style={{
    width: "110px",
    height: "110px",
    transform: `scaleX(${shouldFlip ? -1 : 1})`,
    transformOrigin: "center center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
  <div
    style={{
      width: "110px",
      height: "110px",
      transform: `translate(${lottieConfig.offsetX}px, ${lottieConfig.offsetY}px) scale(${lottieConfig.scale})`,
      transformOrigin: "center center",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Lottie
      animationData={lottieAnimation}
      loop
      autoplay
      rendererSettings={{
        preserveAspectRatio: "xMidYMid meet",
      }}
      style={{
        width: "110px",
        height: "110px",
        zIndex: 2,
      }}
    />
  </div>
</div>
      </div>
    </Marker>
  );
};


const restartAnimation = (id: string, icon: string) => {
  const lineToAnimate = linesGeoJSON.features.find(
    (l) => l.properties?.id === id
  );

  if (!lineToAnimate) return;

  const startIdx = lineToAnimate.properties?.startIdx;
  const endIdx = lineToAnimate.properties?.endIdx;

  const startEtape = etapes[startIdx];
  const endEtape = etapes[endIdx];

  if (!startEtape || !endEtape) return;

  setAnimatedTransport(null);
  setCurrentAnimatingLineId(null);

  setTimeout(() => {
    setCurrentAnimatingLineId(id);

    const transportType = icon.split("-")[0];

    if (["plane", "ship", "train"].includes(transportType)) {
      animateTransportAlongLine(lineToAnimate, transportType);
    } else {
      setHiddenGeodesicLineId(id);
      animateTransportAlongLineWithGps(
        lineToAnimate,
        startEtape,
        endEtape,
        transportType
      );
    }
  }, 0);
};

const displayedLinesGeoJSON: FeatureCollection<LineString> = {
  type: "FeatureCollection",
  features: linesGeoJSON.features.filter(
    (line) => line.properties?.id !== hiddenGeodesicLineId
  ),
};

const MapLegend = () => {
  const legendItems = [
    {
      label: t("lieu"),
      color: MAP_COLORS.lieu,
      icon: "las la-map-marker-alt",
    },
    {
      label: t("repas"),
      color: MAP_COLORS.repas,
      icon: "las la-utensils",
    },
    {
      label: t("trajet"),
      color: MAP_COLORS.trajet,
      icon: "las la-bus",
    },
    {
      label: t("nuit"),
      color: MAP_COLORS.nuit,
      icon: "las la-bed",
    },
    {
      label: t("location"),
      color: MAP_COLORS.location,
      icon: "las la-car",
    },
    {
     label: t("groupe"),
      color: MAP_COLORS.cluster,
      icon: "las la-layer-group",
    },
  ];

  if (!isLegendOpen) {
    return (
     <div
  style={{
    position: "absolute",
    top: "12px",
    left: "12px",
    zIndex: 10,
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    alignItems: "flex-start",
  }}
>
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      setIsLegendOpen((prev) => !prev);
    }}
    className="
      flex
      items-center
      gap-0.5
      lg:gap-1.5
      rounded-[14px]
      border
      border-white/70
      bg-white/95
      px-1
      lg:px-3
      py-1
      lg:py-2
      text-xxs
      lg:text-[11px]
      font-extrabold
      text-slate-800
      shadow-[0_8px_24px_rgba(15,23,42,0.18)]
      backdrop-blur-md
      active:scale-95
      transition
    "
  >
    <i className="las la-info text-xxs lg:text-[15px] text-slate-700" />
    {t("legende")}
  </button>

  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();

      setShowAllMarkerInfos((prev) => {
        const next = !prev;

        if (!next) {
          setOpenedMarkerInfoIds([]);
        }

        return next;
      });
    }}
    className="
      flex
      items-center
      gap-0.5
      lg:gap-1.5
      rounded-[14px]
      border
      border-white/70
      bg-slate-900
      px-2
      lg:px-4
      py-1
      lg:py-2
      text-xxs 
      lg:text-[11px] 
      font-extrabold
      text-white
      shadow-[0_8px_24px_rgba(15,23,42,0.18)]
      backdrop-blur-md
      active:scale-95
      transition
    "
  >
    <i
      className={`las ${
        showAllMarkerInfos ? "la-eye" : "la-eye-slash"
      } text-xxs lg:text-[15px]`}
    />
    {t("libelles")}
  </button>

  {isLegendOpen && <MapLegend />}
</div>
    );
  }

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        setIsLegendOpen(false);
      }}
      className="
        absolute
        z-[30]
        w-[86px]
        lg:w-[95px]
        p-2
        top-1
        lg:top-2
        left-1
        lg:left-2
        rounded-[18px]
        border
        border-white/70
        bg-white/90
        shadow-[0_14px_35px_rgba(15,23,42,0.16)]
        backdrop-blur-md
        cursor-pointer
        transition
      "
      title={t("masquerLegende")}
    >
      <div className="mb-1 flex items-center justify-between px-1">
        <span className="text-xxs lg:text-[10px] font-extrabold text-slate-800">
          {t("legende")}
        </span>
        <i className="las la-times text-xxs lg:text-[12px] text-slate-400" />
      </div>

      <div className="space-y-1">
        {legendItems.map((item) => (
          <div
            key={item.label}
            className="
              flex
              items-center
              gap-1
              rounded-[11px]
              border
              border-slate-100
              bg-white/95
              px-0.5
              lg:px-1
               py-0.5
              lg:py-1
              shadow-[0_5px_14px_rgba(15,23,42,0.10)]
            "
          >
            <div
              className="
                flex
                h-4
                w-4
                shrink-0
                items-center
                justify-center
                rounded-[8px]
                text-white
                shadow-sm
              "
              style={{ backgroundColor: item.color }}
            >
              <i className={`${item.icon} text-xxs lg:text-[12px]`} />
            </div>

            <div className="min-w-0 leading-tight">
              <div
                className="truncate text-xxs lg:text-[10.5px] font-extrabold"
                style={{ color: item.color }}
              >
                {item.label}
              </div>
            </div>
          </div>
        ))}
      </div>
      
    </div>
  );
};
  return (
    <>
      {viewport.longitude !== 0 && viewport.latitude !== 0 && viewport.zoom !== 99 ? (
        <Map
  mapLib={maplibregl as any}
  key={`${viewport.longitude},${viewport.latitude},${viewport.zoom},${selectedCardID}`}
  initialViewState={viewport}
  mapStyle={`https://maps.geoapify.com/v1/styles/osm-bright/style.json?apiKey=${apiKeyGeoapify}`}
          onLoad={handleMapLoad}    
          onZoom={handleZoom}
          ref={mapRef}
          interactiveLayerIds={["animated-transport-layer", "transport-layer"]}
          onClick={(e) => {
  const features = e.features;
  if (!features || features.length === 0) return;

  const feature = features[0];
  if (feature.layer?.id === "animated-transport-layer" || feature.layer?.id === "transport-layer") {
    const id = feature.properties.lineId || selectedLineId;
    if (!id) return;
    setSelectedLineId(id);

    if (isAnimating) {
      alert("Merci d'attendre la fin de la précédente animation.");
      return; // on sort sans lancer la nouvelle animation
    }

      const lineToAnimate = linesGeoJSON.features.find(l => l.properties?.id === id);
  if (!lineToAnimate) return;


    // Récupérer les étapes de départ et d'arrivée
    const startCoordsArray = lineToAnimate.geometry.coordinates[0];
    const endCoordsArray = lineToAnimate.geometry.coordinates[lineToAnimate.geometry.coordinates.length - 1];
const startEtape = etapes[lineToAnimate.properties!.startIdx];
const endEtape = etapes[lineToAnimate.properties!.endIdx];

  
if (supercluster && startEtape) {
  // Récupérer tous les clusters racines
  const rootClusters = supercluster.getClusters([-180, -90, 180, 90], 0);

  let allLeaves: any[] = [];

  rootClusters.forEach(cluster => {
    // Vérifie que cluster.id existe et que c'est bien un number
    const clusterId = typeof cluster.id === 'number' ? cluster.id : undefined;

    if (clusterId !== undefined && cluster.properties.cluster) {
      const leaves = supercluster.getLeaves(clusterId, Infinity);
      allLeaves = allLeaves.concat(leaves);
    } else {
      // Si ce n’est pas un cluster ou id invalide, ajouter directement la feature
      allLeaves.push(cluster);
    }
  });

  // Chercher le marker correspondant à startEtape
  const startMarkerFeature = allLeaves.find(f =>
    f.properties && f.properties.id.toString().split("-")[2] === startEtape.id.toString()
  );

  if (startMarkerFeature) {
    handleMarkerClick(startMarkerFeature);
  } 
}

  // 🚨 Vérification vol intercontinental AVANT tout autre traitement
  const coords = lineToAnimate.geometry.coordinates as [number, number][] | [number, number][][];
  const allCoords: [number, number][] =
    lineToAnimate.geometry.type === "LineString"
      ? (coords as [number, number][])
      : (coords as [number, number][][]).flat();

  const lons = allCoords.map(c => c[0]);
  if (Math.max(...lons) - Math.min(...lons) > 180) {

    return; // ← ici, on sort **avant tout le reste**
  }


    setTransportLabels({
      startLabel: startEtape?.libelle || '',
      startCoords: [startCoordsArray[0], startCoordsArray[1]],
      endLabel: endEtape?.libelle || '',
      endCoords: [endCoordsArray[0], endCoordsArray[1]]
    });

    if (currentAnimatingLineId === id) {
      restartAnimation(id, feature.properties.icon);
    } else {
      setCurrentAnimatingLineId(id);

      let transport=feature.properties.icon.split("-")[0];

       if (["plane", "ship", "train"].includes(transport)) {
            // Pour avion, bateau, train → animation classique
    
            animateTransportAlongLine(lineToAnimate, transport);
        } else {
            setHiddenGeodesicLineId(id);
            // Pour les autres transports → calcul GPS via Geoapify
            animateTransportAlongLineWithGps(lineToAnimate, startEtape, endEtape, transport);
        }
     }
}}
}
        >
          <MapLegend />
          {validCoordinates.length > 1 && (
            <>
           <Source id="lines" type="geojson" data={displayedLinesGeoJSON}>
  {/* contour clair pour que le trajet ressorte mieux */}
  <Layer
    id="route-line-shadow"
    type="line"
    layout={{
      "line-cap": "round",
      "line-join": "round",
    }}
    paint={{
      "line-color": "#FFFFFF",
      "line-width": 4,
      "line-opacity": 0.85,
    }}
  />

  {/* ligne principale */}
  <Layer
    id="route-line-main"
    type="line"
    layout={{
      "line-cap": "round",
      "line-join": "round",
    }}
    paint={{
      "line-color": ROUTE_LINE_COLOR,
      "line-width": 2,
      "line-opacity": 0.85,
    }}
  />


</Source>

{renderRouteArrows()}

            {renderTransportBadges()}
            </>
          )}
          {animatedGpsLine && (
  <Source id="gpsLine" type="geojson" data={animatedGpsLine}>
    <Layer
      id="gpsLineLayer"
      type="line"
      paint={{
        'line-color': '#5f5e5eff',
                  'line-width': 2
        //"line-dasharray": [2, 2] // optionnel pour style en pointillé
      }}
    />
  </Source>
)}
          <Source id="my-data" type="geojson" data={geojson} cluster={true} clusterRadius={10}>
            {/* Vos couches de cluster et de marqueurs */}
          </Source>
         {renderTransportAnimation()}
         {transportLabels.startCoords && (
        <Marker
          longitude={transportLabels.startCoords[0]}
          latitude={transportLabels.startCoords[1]}
          offset={[0, -40]}
           style={{
      background: 'rgba(0, 0, 0, 0.8)', // fond noir semi-transparent
      color: '#fff',                    // texte blanc
      padding: '2px 6px',               // un peu plus d'espace horizontal
      borderRadius: 4,                  // coins arrondis
      fontSize: 12,                     // taille du texte
      fontWeight: 400,                  // écriture fine
      boxShadow: '0 2px 6px rgba(0,0,0,0.3)', // légère ombre
      whiteSpace: 'nowrap',             // pour que le texte reste sur une ligne
      pointerEvents: 'none'   ,          // évite d'interférer avec les clics sur la carte
       zIndex: 1 
    }}
        >
          {transportLabels.startLabel}
        </Marker>
      )}

      {transportLabels.endCoords && (
        <Marker
          longitude={transportLabels.endCoords[0]}
          latitude={transportLabels.endCoords[1]}
          offset={[0, -40]}
           style={{
      background: 'rgba(0, 0, 0, 0.8)', // fond noir semi-transparent
      color: '#fff',                    // texte blanc
      padding: '2px 6px',               // un peu plus d'espace horizontal
      borderRadius: 4,                  // coins arrondis
      fontSize: 12,                     // taille du texte
      fontWeight: 400,                  // écriture fine
      boxShadow: '0 2px 6px rgba(0,0,0,0.3)', // légère ombre
      whiteSpace: 'nowrap',             // pour que le texte reste sur une ligne
      pointerEvents: 'none' ,            // évite d'interférer avec les clics sur la carte
       zIndex: 1 
    }}
        >
          {transportLabels.endLabel}
        </Marker>
      )}
          {isMapLoad && visibleClusters.length > 0 && renderClusters()}
          <NavigationControl />
          <FullscreenControl />
        </Map>

        
      ) : null}

      {isLoading && (
        <Transition appear show={true} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 z-[100] overflow-y-auto" // Plus élevé pour l'attente
            onClose={() => {}} // Pas de fermeture pour la modale d'attente
          >
            <div className="min-h-screen flex items-center justify-center px-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-60" />
              </Transition.Child>
              
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0 scale-95"
              >
                <div
                  className="inline-block transition-all transform rounded-2xl bg-white dark:bg-neutral-900 dark:border dark:border-neutral-700 dark:text-neutral-100 shadow-xl px-8 py-4"
                >
                  <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-neutral-200">
                    {t("calculItineraireEnCours")}
                  </h3>
                </div>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition>
      )}
    </>
  );
}

export default MapboxGL;
